import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { AuroraBackground } from "@/components/AuroraBackground";
import { FloatingButtons } from "@/components/FloatingButtons";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { renderWithSuperintelligenceItalic } from "@/lib/superintelligence";

const WHATSAPP = "https://wa.me/5511963242299";

export interface PillarSection {
  h2: string;
  paragraphs?: string[];
  bullets?: string[];
  /** tabela comparativa opcional */
  table?: { head: string[]; rows: string[][] };
}

export interface PillarPageProps {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  lead: string;
  /** resposta direta no topo — usada por Google e IAs generativas */
  answer: string;
  sections: PillarSection[];
  faq: { q: string; a: string }[];
  related: { label: string; to: string }[];
  heroMark?: { src: string; alt: string };
  extraJsonLd?: Record<string, unknown>[];
}

export function PillarPage(p: PillarPageProps) {
  const url = `https://op2p.ai/${p.slug}`;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://op2p.ai/" },
      { "@type": "ListItem", position: 2, name: p.h1, item: url },
    ],
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: p.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background relative">
      <SEO
        title={p.metaTitle}
        description={p.metaDescription}
        path={`/${p.slug}`}
        type="website"
        jsonLd={[breadcrumbLd, faqLd, ...(p.extraJsonLd ?? [])]}
      />
      <AuroraBackground />
      <FloatingButtons />

      <main className="relative z-10">
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: p.h1 }]} />
              {p.heroMark && (
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-card shadow-sm md:h-20 md:w-20">
                  <img
                    src={p.heroMark.src}
                    alt={p.heroMark.alt}
                    width="64"
                    height="64"
                    className="h-10 w-10 object-contain md:h-12 md:w-12"
                  />
                </div>
              )}
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-6">{p.h1}</h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-10">{renderWithSuperintelligenceItalic(p.lead)}</p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="rounded-full">
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 w-4 h-4" /> Falar com especialista
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <Link to="/agenda">
                    Agendar demonstração <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Resposta direta */}
        <section className="py-12 bg-secondary">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 [&>*]:max-w-3xl">
            <p className="text-lg md:text-xl text-foreground leading-relaxed">{renderWithSuperintelligenceItalic(p.answer)}</p>
          </div>
        </section>

        {p.sections.map((s, i) => (
          <section key={s.h2} className={i % 2 === 1 ? "py-16 bg-secondary" : "py-16"}>
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 [&>*]:max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{s.h2}</h2>
              {s.paragraphs?.map((t) => (
                <p key={t} className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                  {t}
                </p>
              ))}
              {s.bullets && (
                <ul className="space-y-3 mt-4">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-3 items-start">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span className="text-base text-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.table && (
                <div className="overflow-x-auto mt-6 rounded-2xl border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-background">
                      <tr>
                        {s.table.head.map((h) => (
                          <th key={h} className="p-4 font-bold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((r) => (
                        <tr key={r.join()} className="border-t border-border">
                          {r.map((c) => (
                            <td key={c} className="p-4 text-muted-foreground">
                              {c}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        ))}

        <section className="py-16">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 [&>*]:max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-10">Perguntas frequentes</h2>
            <div className="space-y-6">
              {p.faq.map((f) => (
                <div key={f.q} className="border-b border-border pb-6">
                  <h3 className="text-lg font-bold mb-2">{f.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 [&>*]:max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Continue explorando</h2>
            <div className="flex flex-wrap gap-3">
              {p.related.map((l) => (
                <Link
                  key={l.to + l.label}
                  to={l.to}
                  className="text-sm font-medium rounded-full border border-border px-4 py-2 hover:bg-background transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-black text-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center [&>*]:max-w-3xl [&>*]:mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Quer ver funcionando no seu negócio?</h2>
            <p className="text-lg text-white/70 mb-10">
              Nossa equipe configura o CRM e os Agentes de IA da OP2P para a sua operação. Fale com um especialista e veja
              uma demonstração real.
            </p>
            <Button asChild size="lg" className="rounded-full">
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 w-4 h-4" /> Falar no WhatsApp
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
